import { useMemo, useState } from "react";
import { EditSubCategory } from "../../../types";
import { Accordion, Badge, Button, ButtonGroup, Modal } from "react-bootstrap";
import CategoryFormModal, { CategoryFormData } from "./CategoryFormModal";
import { useCreateCategory } from "../../../hooks/useCreateCategory";
import { useUpdateCategory } from "../../../hooks/useUpdateCategory";
import { useToggleCategoryActive } from "../../../hooks/useToggleCategoryActive";

const LANGUAGES = [
	{ key: "en-us", label: "EN" },
	{ key: "de-ch", label: "DE-CH" },
	{ key: "de-de", label: "DE-DE" },
	{ key: "fr-fr", label: "FR" },
	{ key: "it-it", label: "IT" },
] as const;

type LanguageKey = (typeof LANGUAGES)[number]["key"];

interface ParsedCategory {
	categoryId: string;
	sequence: number;
	isDeleted: boolean;
	translations: Record<LanguageKey, string>;
}

interface ParsedSubcategory extends ParsedCategory {
	parentCategoryId: string;
}

interface Props {
	editCategories: EditSubCategory[];
	onRefresh: () => void;
}

function buildTranslations(item: EditSubCategory): Record<LanguageKey, string> {
	return {
		"en-us": item["en-us"] ?? "",
		"de-ch": item["de-ch"] ?? "",
		"de-de": item["de-de"] ?? "",
		"fr-fr": item["fr-fr"] ?? "",
		"it-it": item["it-it"] ?? "",
	};
}

type StatusFilter = "all" | "active" | "inactive";

export default function Categories({ editCategories, onRefresh }: Props) {
	const createCategory = useCreateCategory();
	const updateCategory = useUpdateCategory();
	const toggleActive = useToggleCategoryActive();

	const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
	const [searchText, setSearchText] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [modalTitle, setModalTitle] = useState("");
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingParentId, setEditingParentId] = useState<string | null>(null);
	const [modalDefaults, setModalDefaults] = useState<CategoryFormData | undefined>(undefined);
	const [showConfirm, setShowConfirm] = useState(false);
	const [confirmTarget, setConfirmTarget] = useState<{ id: string; isActive: boolean; name: string } | null>(null);
	const { categories, subcategories } = useMemo(() => {
		// Collect all unique parent IDs (ItemCategory_fk values)
		const parentIdSet = new Set<string>();
		editCategories.forEach((item) => {
			if (item.ItemCategory_fk) {
				parentIdSet.add(item.ItemCategory_fk);
			}
		});

		// Index all items by their ID for quick lookup
		const itemById = new Map<string, EditSubCategory>();
		editCategories.forEach((item) => {
			itemById.set(item.ItemCategory_ID, item);
		});

		// Categories = items that are parents (referenced by others) OR have no parent themselves
		// Subcategories = items that have a parent and are not themselves a parent
		const categories: ParsedCategory[] = [];
		const subcategories: ParsedSubcategory[] = [];

		editCategories.forEach((item) => {
			if (parentIdSet.has(item.ItemCategory_ID) || !item.ItemCategory_fk) {
				// This item is a parent category (has children or has no parent)
				categories.push({
					categoryId: item.ItemCategory_ID,
					sequence: item.CustomSequence ?? item.DefaultSequence,
					isDeleted: item.IsDeleted,
					translations: buildTranslations(item),
				});
			} else {
				// This item is a subcategory
				subcategories.push({
					categoryId: item.ItemCategory_ID,
					sequence: item.CustomSequence ?? item.DefaultSequence,
					isDeleted: item.IsDeleted,
					translations: buildTranslations(item),
					parentCategoryId: item.ItemCategory_fk,
				});
			}
		});

		// Also add parent IDs that don't exist as rows (orphan parents)
		parentIdSet.forEach((parentId) => {
			if (!itemById.has(parentId)) {
				categories.push({
					categoryId: parentId,
					sequence: 0,
					isDeleted: false,
					translations: { "en-us": parentId, "de-ch": "", "de-de": "", "fr-fr": "", "it-it": "" },
				});
			}
		});

		return { categories, subcategories };
	}, [editCategories]);

	const sorted = categories.sort((a, b) => a.sequence - b.sequence);

	const filtered = sorted.filter((cat) => {
		if (statusFilter === "active" && cat.isDeleted) return false;
		if (statusFilter === "inactive" && !cat.isDeleted) return false;

		if (searchText) {
			const term = searchText.toLowerCase();
			const catMatch = Object.values(cat.translations).some((v) => v.toLowerCase().includes(term));
			const subsMatch = subcategories
				.filter((sub) => sub.parentCategoryId === cat.categoryId)
				.some((sub) => Object.values(sub.translations).some((v) => v.toLowerCase().includes(term)));
			if (!catMatch && !subsMatch) return false;
		}

		return true;
	});

	const handleEdit = (categoryId: string) => {
		const item = editCategories.find((c) => c.ItemCategory_ID === categoryId);
		const name = item?.["en-us"] || item?.["de-ch"] || item?.["de-de"] || categoryId;
		setEditingId(categoryId);
		setEditingParentId(null);
		setModalTitle(`Edit: ${name}`);
		setModalDefaults(
			item
				? { "en-us": item["en-us"] ?? "", "de-ch": item["de-ch"] ?? "", "de-de": item["de-de"] ?? "", "fr-fr": item["fr-fr"] ?? "", "it-it": item["it-it"] ?? "" }
				: undefined,
		);
		setShowModal(true);
	};

	const handleToggleActive = (categoryId: string, currentlyActive: boolean) => {
		const item = editCategories.find((c) => c.ItemCategory_ID === categoryId);
		const name = item?.["en-us"] || item?.["de-ch"] || categoryId;
		setConfirmTarget({ id: categoryId, isActive: currentlyActive, name });
		setShowConfirm(true);
	};

	const confirmToggleActive = () => {
		if (!confirmTarget) return;
		toggleActive.mutate(
			{
				itemCategoryId: confirmTarget.id,
				isDeleted: confirmTarget.isActive,
			},
			{ onSuccess: () => onRefresh() },
		);
		setShowConfirm(false);
		setConfirmTarget(null);
	};

	const handleAddCategory = () => {
		setEditingId(null);
		setEditingParentId(null);
		setModalTitle("New Category");
		setModalDefaults(undefined);
		setShowModal(true);
	};

	const handleAddSubcategory = (parentCategoryId: string) => {
		const parent = editCategories.find((c) => c.ItemCategory_ID === parentCategoryId);
		const parentName = parent?.["en-us"] || parent?.["de-ch"] || parent?.["de-de"] || parentCategoryId;
		setEditingId(null);
		setEditingParentId(parentCategoryId);
		setModalTitle(`New Subcategory in: ${parentName}`);
		setModalDefaults(undefined);
		setShowModal(true);
	};

	const handleFormSubmit = (data: CategoryFormData) => {
		const nextSequence = categories.length + 1;

		if (editingId) {
			const item = editCategories.find((c) => c.ItemCategory_ID === editingId);
			updateCategory.mutate(
				{
					itemCategoryId: editingId,
					localizations: data,
					customSequence: item?.CustomSequence ?? nextSequence,
					defaultSequence: item?.DefaultSequence ?? nextSequence,
					legacyValue: item?.LegacyValue ?? null,
				},
				{ onSuccess: () => onRefresh() },
			);
		} else {
			createCategory.mutate(
				{
					localizations: data,
					customSequence: nextSequence,
					defaultSequence: nextSequence,
					legacyValue: null,
					parentCategoryFk: editingParentId,
				},
				{
					onSuccess: () => {
						setStatusFilter("inactive");
						onRefresh();
					},
				},
			);
		}
	};

	const displayName = (translations: Record<LanguageKey, string>) => {
		return translations["en-us"] || Object.values(translations).find((v) => v) || "—";
	};

	return (
		<div className="card border-0 shadow-sm">
			<div className="card-body p-0">
				<div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
					<div className="d-flex align-items-center gap-2">
						<small className="text-muted">{filtered.length} of {sorted.length} categories</small>
						<Button
							variant="primary"
							size="sm"
							className="d-flex align-items-center gap-1"
							onClick={handleAddCategory}
						>
							<i className="bi bi-plus-lg"></i>
							New Category
						</Button>
					</div>
					<div className="d-flex align-items-center gap-2">
						<div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
							<span className="input-group-text bg-white border-end-0">
								<i className="bi bi-search text-muted"></i>
							</span>
							<input
								type="text"
								className="form-control border-start-0"
								placeholder="Search..."
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
							/>
						</div>
						<ButtonGroup size="sm">
							<Button
								variant={statusFilter === "all" ? "primary" : "outline-secondary"}
								onClick={() => setStatusFilter("all")}
							>
								All
							</Button>
							<Button
								variant={statusFilter === "active" ? "primary" : "outline-secondary"}
								onClick={() => setStatusFilter("active")}
							>
								Active
							</Button>
							<Button
								variant={statusFilter === "inactive" ? "primary" : "outline-secondary"}
								onClick={() => setStatusFilter("inactive")}
							>
								Inactive
							</Button>
						</ButtonGroup>
					</div>
				</div>
				<Accordion alwaysOpen flush>
					{filtered.map((cat) => {
						const subs = subcategories
							.filter((sub) => sub.parentCategoryId === cat.categoryId)
							.sort((a, b) => a.sequence - b.sequence);

						const isActive = !cat.isDeleted;

						return (
							<Accordion.Item
								eventKey={cat.categoryId}
								key={cat.categoryId}
								className={!isActive ? "opacity-75" : ""}
							>
								<Accordion.Header>
									<div className="d-flex align-items-center justify-content-between w-100 me-3">
										<div className="d-flex align-items-center gap-2">
											<span className="fw-semibold">{displayName(cat.translations)}</span>
											<Badge bg="light" text="dark" pill className="border">
												{subs.length} sub
											</Badge>
										</div>
										<div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
											<Button
												variant="outline-primary"
												size="sm"
												className="d-flex align-items-center gap-1 py-0 px-1"
												onClick={() => handleEdit(cat.categoryId)}
											>
												<i className="bi bi-pencil-square"></i>
											</Button>
											<Button
												variant={isActive ? "outline-danger" : "outline-success"}
												size="sm"
												className="d-flex align-items-center gap-1 py-0 px-1"
												onClick={() => handleToggleActive(cat.categoryId, isActive)}
											>
												<i className={`bi ${isActive ? "bi-toggle-on" : "bi-toggle-off"}`}></i>
											</Button>
											<span
												className={`d-inline-block rounded-circle ${isActive ? "bg-success" : "bg-danger"}`}
												style={{ width: 8, height: 8 }}
											></span>
										</div>
									</div>
								</Accordion.Header>
								<Accordion.Body className="bg-light bg-opacity-50">
									<div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
										{/* Translations */}
										<div className="d-flex flex-wrap gap-2">
											{LANGUAGES.map((lang) => (
												<span key={lang.key} className="badge bg-white text-dark border px-2 py-1">
													<span className="text-muted me-1">{lang.label}:</span>
													{cat.translations[lang.key] || <span className="text-muted fst-italic">—</span>}
												</span>
											))}
										</div>
										<Button
											variant="outline-primary"
											size="sm"
											className="d-flex align-items-center gap-1 ms-2 flex-shrink-0"
											onClick={() => handleAddSubcategory(cat.categoryId)}
										>
											<i className="bi bi-plus-lg"></i>
											Add Subcategory
										</Button>
									</div>

									{subs.length > 0 ? (
										<div className="d-flex flex-column gap-2">
											{subs.map((sub) => {
												const subActive = !sub.isDeleted;
												return (
													<div
														key={sub.categoryId}
														className="rounded border bg-white px-3 py-2"
													>
														<div className="d-flex align-items-center justify-content-between">
															<div className={`d-flex align-items-center gap-2 ${!subActive ? "opacity-50" : ""}`}>
																<i className="bi bi-tag text-secondary"></i>
																<span>{displayName(sub.translations)}</span>
																<span
																	className={`d-inline-block rounded-circle ${subActive ? "bg-success" : "bg-danger"}`}
																	style={{ width: 6, height: 6 }}
																></span>
															</div>
															<div className="d-flex gap-1">
																<Button
																	variant="link"
																	size="sm"
																	className="text-primary p-1 lh-1"
																	title="Edit"
																	onClick={() => handleEdit(sub.categoryId)}
																>
																	<i className="bi bi-pencil-square"></i>
																</Button>
																<Button
																	variant="link"
																	size="sm"
																	className={`p-1 lh-1 ${subActive ? "text-danger" : "text-success"}`}
																	title={subActive ? "Deactivate" : "Activate"}
																	onClick={() => handleToggleActive(sub.categoryId, subActive)}
																>
																	<i className={`bi ${subActive ? "bi-toggle-on" : "bi-toggle-off"}`}></i>
																</Button>
															</div>
														</div>
														{/* Sub translations */}
														<div className="d-flex flex-wrap gap-1 mt-1">
															{LANGUAGES.map((lang) => (
																<small key={lang.key} className="text-muted">
																	<span className="fw-semibold">{lang.label}:</span>{" "}
																	{sub.translations[lang.key] || "—"}
																</small>
															))}
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<div className="text-center text-muted py-3">
											<i className="bi bi-inbox me-1"></i>
											No subcategories
										</div>
									)}
								</Accordion.Body>
							</Accordion.Item>
						);
					})}
				</Accordion>
			</div>

			<CategoryFormModal
				show={showModal}
				onHide={() => setShowModal(false)}
				onSubmit={handleFormSubmit}
				title={modalTitle}
				defaultValues={modalDefaults}
			/>

			<Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Confirm</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{confirmTarget?.isActive
						? `Deactivate "${confirmTarget.name}"?`
						: `Activate "${confirmTarget?.name}"?`}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" size="sm" onClick={() => setShowConfirm(false)}>
						Cancel
					</Button>
					<Button
						variant={confirmTarget?.isActive ? "danger" : "success"}
						size="sm"
						onClick={confirmToggleActive}
					>
						{confirmTarget?.isActive ? "Deactivate" : "Activate"}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
